import { useState } from "react";
import { supabase } from "@/lib/supabase";
import type { AdminOrderData, } from "../page";
import { STATUS_OPTIONS } from "../page";
import type { SavedOrder } from "@/hooks/useOrders";

interface Props {
  order: AdminOrderData;
  onStatusChange: (status: SavedOrder["status"]) => Promise<void>;
  updating: boolean;
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function AdminOrderActions({ order, onStatusChange, updating, onToast }: Props) {
  const [sendingWa, setSendingWa] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const currentSt = STATUS_OPTIONS.find(s => s.value === order.status) ?? STATUS_OPTIONS[0];
  const isCancelled = order.status === "cancelled";

  const handleWhatsApp = async () => {
    setSendingWa(true);
    try {
      const { data } = await supabase.functions.invoke("whatsapp-notify", {
        body: {
          type: "status_update",
          orderRef: order.orderRef,
          phone: order.phone,
          name: order.name,
          city: order.city,
          status: order.status,
          items: order.items,
          finalPrice: order.finalPrice,
          paymentLabel: order.paymentLabel,
        },
      });
      if (data?.autoSent) {
        onToast("تم إرسال إشعار واتساب للعميل ✓");
      } else if (data?.whatsappUrl) {
        window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
        onToast("تم فتح واتساب للإرسال اليدوي");
      } else {
        onToast("تعذّر إرسال واتساب", "error");
      }
    } catch {
      onToast("حدث خطأ", "error");
    }
    setSendingWa(false);
  };

  const handleEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await supabase.functions.invoke("send-order-email", {
        body: {
          orderRef: order.orderRef,
          phone: order.phone,
          name: order.name,
          status: order.status,
          items: order.items,
          finalPrice: order.finalPrice,
          paymentLabel: order.paymentLabel,
          city: order.city,
        },
      });
      if (data?.sent) {
        onToast("تم إرسال البريد الإلكتروني للعميل ✓");
      } else {
        onToast("تعذّر إرسال البريد", "error");
      }
    } catch {
      onToast("حدث خطأ", "error");
    }
    setSendingEmail(false);
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 flex flex-col gap-4">
      <h3 className="font-bold text-[#1a1a1a] text-sm flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg">
          <i className="ri-settings-3-line text-[#1a1a1a] text-sm"></i>
        </div>
        إجراءات الطلب
      </h3>

      {/* Current status */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${currentSt.bg} ${currentSt.border}`}>
        <i className={`${currentSt.icon} ${currentSt.color} text-lg`}></i>
        <div>
          <p className="text-xs text-gray-500">الحالة الحالية</p>
          <p className={`text-sm font-bold ${currentSt.color}`}>{currentSt.label}</p>
        </div>
      </div>

      {/* Status change */}
      {!isCancelled && (
        <div>
          <p className="text-xs font-bold text-gray-400 mb-2">تغيير الحالة</p>
          <div className="flex flex-col gap-2">
            {STATUS_OPTIONS.filter(s => s.value !== "cancelled" && s.value !== order.status).map(s => (
              <button
                key={s.value}
                onClick={() => onStatusChange(s.value as SavedOrder["status"])}
                disabled={updating}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-bold transition-all cursor-pointer disabled:opacity-50 hover:scale-[1.01] ${s.bg} ${s.border} ${s.color}`}
              >
                {updating ? (
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                ) : (
                  <i className={`${s.icon} text-base`}></i>
                )}
                {s.label}
                <i className="ri-arrow-left-s-line mr-auto text-sm opacity-50"></i>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-100"></div>

      {/* Notification buttons */}
      <div>
        <p className="text-xs font-bold text-gray-400 mb-2">إشعارات العميل</p>
        <div className="flex flex-col gap-2">
          <button
            onClick={handleWhatsApp}
            disabled={sendingWa}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-green-200 bg-green-50 text-green-700 text-sm font-bold transition-all cursor-pointer disabled:opacity-50 hover:bg-green-100"
          >
            {sendingWa ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-whatsapp-line text-base"></i>
            )}
            إرسال واتساب
          </button>

          <button
            onClick={handleEmail}
            disabled={sendingEmail}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-bold transition-all cursor-pointer disabled:opacity-50 hover:bg-indigo-100"
          >
            {sendingEmail ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-mail-send-line text-base"></i>
            )}
            إرسال بريد إلكتروني
          </button>

          <a
            href={`https://wa.me/${order.phone.replace(/^0/, "966")}?text=${encodeURIComponent(`مرحباً ${order.name}، بخصوص طلبك #${order.orderRef}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 text-sm font-bold transition-all cursor-pointer hover:bg-gray-100"
          >
            <i className="ri-chat-3-line text-base"></i>
            محادثة مباشرة
          </a>
        </div>
      </div>
    </div>
  );
}
